import { findBondSecurityByIsin } from "@/store/bondSecuritiesStoreACTIONS";
import { REPO_TICKET_DIRECTIONS } from "../helper/repoTicketHelper";
import { ITicketFieldsProps } from "../helper/ticketSharedHelper";
import { useBondSecuritySearch } from "../helper/useBondSecuritySearch";
import DirectionSelect from "../ticketFields/DirectionSelect";
import NominalInput from "../ticketFields/NominalInput";
import ReferenceDataSelect, {
  IReferenceDataOption,
} from "../ticketFields/ReferenceDataSelect";
import TicketDateInput from "../ticketFields/TicketDateInput";
import TicketRow from "../ticketFields/TicketRow";
import AllocationSplits from "../ticketFields/AllocationSplits";
import TicketExternalIdRow from "../ticketFields/TicketExternalIdRow";
import { useRepoTicket } from "./useRepoTicket";
import { TicketInstrument } from "@/store/tradeFromScratchStore";
import { returnSettlementHolidayCalendars } from "../helper/holidayCalendarsHelper";

// The repo fields a Trade Creation leg row shows inline. Start Date is the settlement
// date - the API has no separate field.
const RepoCoreFields = ({ ticketId, highlightedKeys }: ITicketFieldsProps) => {
  const { ticket, updateRepoTicket, dateIssue, requiredKeys } =
    useRepoTicket(ticketId);
  const { searchIsinOptions, searchSecurityNameOptions } =
    useBondSecuritySearch();

  const isMissing = (key: string) => !!highlightedKeys?.has(key);
  const isRequired = (key: string) => requiredKeys.has(key);

  const handleCollateralSelect = (option: IReferenceDataOption | null) => {
    if (!option) {
      updateRepoTicket({ isin: "", securityName: "" });
      return;
    }
    updateRepoTicket({
      isin: option.value,
      securityName: findBondSecurityByIsin(option.value)?.securityName ?? "",
    });
  };

  return (
    <div
      data-testid="RepoCoreFieldsContainer"
      id="RepoCoreFieldsContainer"
      className="flex flex-col gap-2"
    >
      <TicketRow
        label="Collateral ISIN"
        testId="RepoTicketIsinRow"
        isHighlighted={isMissing("isin")}
        isRequired={isRequired("isin")}
      >
        <ReferenceDataSelect
          testId="RepoTicketIsinInput"
          placeholder="Search ISIN ..."
          selected={
            ticket.isin ? { label: ticket.isin, value: ticket.isin } : null
          }
          searchOptions={searchIsinOptions}
          onSelect={handleCollateralSelect}
        />
      </TicketRow>

      <TicketRow
        label="Collateral"
        testId="RepoTicketSecurityNameRow"
        isHighlighted={isMissing("isin")}
        isRequired={isRequired("isin")}
      >
        <ReferenceDataSelect
          testId="RepoTicketSecurityNameInput"
          placeholder="Search security ..."
          selected={
            ticket.isin
              ? { label: ticket.securityName, value: ticket.isin }
              : null
          }
          searchOptions={searchSecurityNameOptions}
          onSelect={handleCollateralSelect}
        />
      </TicketRow>

      <TicketRow
        label="Nominal"
        testId="RepoTicketNominalRow"
        isHighlighted={isMissing("nominal")}
        isRequired={isRequired("nominal")}
      >
        <NominalInput
          testId="RepoTicketNominalInput"
          value={ticket.nominal}
          onCommit={(nominal) => updateRepoTicket({ nominal })}
        />
      </TicketRow>

      <AllocationSplits
        testIdPrefix="RepoTicket"
        portfolioCode={ticket.portfolioCode}
        onPortfolioChange={(portfolioCode) => updateRepoTicket({ portfolioCode })}
        isPortfolioRequired={isRequired("portfolioCode")}
        total={ticket.nominal}
        splits={ticket.additionalAllocations}
        sizeKind="nominal"
        highlightedKeys={highlightedKeys}
        onChange={(additionalAllocations) => updateRepoTicket({ additionalAllocations })}
      />

      <TicketRow
        label="Direction"
        testId="RepoTicketDirectionRow"
        isHighlighted={isMissing("direction")}
        isRequired={isRequired("direction")}
      >
        <DirectionSelect
          testId="RepoTicketDirectionInput"
          value={ticket.direction}
          directions={REPO_TICKET_DIRECTIONS}
          onChange={(direction) => updateRepoTicket({ direction })}
        />
      </TicketRow>

      <TicketRow
        label="Start Date"
        testId="RepoTicketStartDateRow"
        isHighlighted={isMissing("settlementDate")}
        isRequired={isRequired("settlementDate")}
        error={dateIssue?.key === "settlementDate" ? dateIssue.message : ""}
      >
        <TicketDateInput
          testId="RepoTicketStartDateInput"
          holidayCalendars={returnSettlementHolidayCalendars(TicketInstrument.Repo, ticket)}
          value={ticket.settlementDate}
          onChange={(settlementDate) => updateRepoTicket({ settlementDate })}
        />
      </TicketRow>

      <TicketExternalIdRow
        testIdPrefix="RepoTicket"
        value={ticket.transactionNumber}
        onChange={(transactionNumber) => updateRepoTicket({ transactionNumber })}
      />
    </div>
  );
};

export default RepoCoreFields;
